import {
  SyncOutlined,
  DownloadOutlined,
  UploadOutlined,
  SaveOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { setLoading } from "../../../../../redux/ducks/Loading";
import { exportFile, showError, showSuccess, trimData } from "../../../../../utils/utilities";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ProjectCostPlan from "../../../../../services/api/ProjectCostPlan";
import { useParams } from "react-router-dom";
import { get, indexOf, isEmpty } from "lodash";
import { fileFormatXextension } from "../../../../../utils/properties";
import WCTable from "./WCTable";
import TotalWCTable from "./TotalWCTable";
import Button from "../../../../../components/functional_components/Button/PFButton";
import { Col, Modal, Row, Spin, Upload, notification } from "antd";
import { initExpandedKeys } from "../../../../../redux/ducks/EditableTable";
import ProjectPlan from "../../../../../services/api/ProjectPlan";
import ImportPlanning from "../../../../../services/api/ImportPlanning";
import Project from "../../../../../services/api/Project";
import TimesheetApprovalApi from "../../../../../services/api/TimesheetApprovalApi";
import {
  setProject,
  setRefreshWorkCosts,
  setRefreshWorkCostsTotals,
  setRdaJobRunning,
  setRunningCostJobRunning,
} from "../../../../../redux/ducks/ProjectManagement";

const { confirm } = Modal;
const { Dragger } = Upload;

const WorkCosts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const refreshWC = useSelector((state) => state.ProjectManagementDuck.refreshWorkCost);
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const isDirtyWC = useSelector((state) => get(state.EditableTableDuck, "isDirtyWC", false));
  const params = useParams();
  const [state, setState] = useState({
    filterLoading: false,
    downloadLoading: false,
    downloadTimesheetReportLoading: false,
    dataTable: [],
    totalAmountValues: [],
    allData: {},
    loading: false,
    importCostsPlanModal: false,
    fileList: [],
    rdaAlertProjectTaskIds: [], // project task ids where RDA > EAC (committed > planned) => cannot save values or approve planning
    plannedRunningErrorProjectTaskIds: [], // project task ids where some running value > planned value => cannot save values or approve planning
  });

  const totalTableRef = useRef(null);
  const [totalTableExpandedChange, setTotalTableExpandedChange] = useState(false); // flag that changes value when the total table's expanded content changes

  useEffect(() => {
    if (refreshWC === null || refreshWC === true) {
      checkRunningJobs();
      dataList();
      dispatch(setRefreshWorkCosts(false));
      dispatch(setRefreshWorkCostsTotals(true));
    }
  }, [refreshWC]);

  const dataList = async () => {
    dispatch(setLoading(true));
    const data = await ProjectCostPlan.getProjectCostPlan(params.id);
    if (!data.error && data.data && data.data.projectCostPlanValues) {
      data.data.projectCostPlanValues.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
        cc.amountValues.forEach((item) => {
          if (item.year === data.data.openYear) {
            item.columnValueMap.PRESENT = cc.consAmountOpenMonth;
          }
        });
      });
      data.data.key = 0;
    }
    const dataTotal = await ProjectCostPlan.projectCostPlanTotal(params.id);
    if (!dataTotal.error && dataTotal.data) {
      dataTotal.data.key = 0;
      const totalAmountValues = [];
      totalAmountValues.push(dataTotal.data);
      setState((prevState) => ({
        ...prevState,
        dataTable: get(data.data, "projectCostPlanValues", []),
        allData: get(data, "data", {}),
        totalAmountValues: totalAmountValues,
      }));
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
    // return { list: get(data, "data", []), tableContentInvoicePlan, tableContentProductionValue }
  };

  const projectDetail = async (id) => {
    dispatch(setLoading(true));
    const infoProject = await Project.getInfoProject(id);
    if (!infoProject.error) {
      dispatch(setProject(get(infoProject, "data", {})));
    } else {
      showError(get(infoProject, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleRelease = async () => {
    confirm({
      title: t("projectsManagement.planning.billingPlan.ensureApprove"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        let isSaveAllowed = true;
        if (!isEmpty(state.rdaAlertProjectTaskIds)) {
          showError("", t("projectsManagement.planning.workCostsTab.rdaAlertErrorMessage"));
          isSaveAllowed = false;
        }
        if (!isEmpty(state.plannedRunningErrorProjectTaskIds)) {
          showError("", t("projectsManagement.planning.workCostsTab.plannedRunningErrorMessage"));
          isSaveAllowed = false;
        }

        dispatch(setLoading(true));

        const response = await ProjectPlan.approveProjectPlan(params.id, "planType=COST_PLAN");
        if (!response.error) {
          if (isSaveAllowed) {
            showSuccess();
          } else {
            notification.open({
              duration: 5,
              className: "notification-success",
              message: t("projectsManagement.planning.workCostsTab.saveSuccessTemporarilyPermittedTitle"),
              description: t("projectsManagement.planning.workCostsTab.releaseSuccessTemporarilyPermitted"),
            });
          }
          dispatch(initExpandedKeys());
          dataList();
          projectDetail(params.id);
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const formikDocument = useFormik({
    initialValues: { file: "" },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.file) errors.file = "Required!";
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async () => {
      const formData = new FormData();
      if (isEmpty(state.fileList)) {
        const fakeFile = new File([], "fakefile.txt");
        formData.append("file", fakeFile);
      } else {
        state.fileList.forEach((file) => {
          formData.append("file", file);
        });
      }
      setState((prevState) => ({ ...prevState, loading: true }));
      const response = await ImportPlanning.uploadCostsPlanDetail(params.id, formData);
      if (!response.error) {
        showSuccess();
        formikDocument.resetForm();
        dataList();
      } else showError(get(response, "error.messageCode", "ER000"));
      setState((prevState) => ({
        ...prevState,
        importCostsPlanModal: false,
        loading: false,
        fileList: [],
      }));
    },
  });

  const uploadInvoice = {
    multiple: false,
    onRemove: (file) => {
      const index = state.fileList.indexOf(file);
      const newFileList = state.fileList.slice();
      newFileList.splice(index, 1);
      setState((prevState) => ({ ...prevState, fileList: newFileList }));
      formikDocument.setFieldValue("file", "");
    },
    beforeUpload: (file) => {
      let ckFileList = [];
      let ckFileValue = "";
      if (indexOf(fileFormatXextension["csv"], file.type) !== -1) {
        ((ckFileList = [file]), (ckFileValue = "ok"));
      } else {
        showError(t("projectEstimate.modal.errorFileFormat"));
      }
      setState((prevState) => ({ ...prevState, fileList: ckFileList }));
      formikDocument.setFieldValue("file", ckFileValue);
      return false;
    },
    fileList: state.fileList,
  };

  const handleExport = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await ProjectCostPlan.projectCostPlanExport(params.id);
    if (!exportResponse.error) {
      const downloadType = fileFormatXextension["xlsx"];
      const blob = new Blob([exportResponse], { type: downloadType });
      exportFile(blob, "work-costs.xlsx");
      showSuccess();
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  const handleExportTimesheetReport = async () => {
    setState((prevState) => ({ ...prevState, downloadTimesheetReportLoading: true }));
    const exportResponse = await TimesheetApprovalApi.exportPlannedVsTimesheetReport(`projectIds=${params.id}`);
    if (!exportResponse.error) {
      const downloadType = fileFormatXextension["xlsx"];
      const blob = new Blob([exportResponse], { type: downloadType });
      exportFile(blob, "planned-vs-timesheet.xlsx");
      showSuccess();
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadTimesheetReportLoading: false }));
  };

  const setRdaAlertProjectTaskIds = useCallback(
    (ids) => setState((prev) => ({ ...prev, rdaAlertProjectTaskIds: ids })),
    []
  );

  const setPlannedRunningErrorProjectTaskIds = useCallback(
    (idsOrFunction) =>
      setState((prev) => ({
        ...prev,
        plannedRunningErrorProjectTaskIds:
          typeof idsOrFunction === "function" ? idsOrFunction(prev.plannedRunningErrorProjectTaskIds) : idsOrFunction,
      })),
    []
  );

  const checkRunningJobs = async () => {
    // Check if the jobs that periodically update the running and committed values are running
    const response = await ImportPlanning.lastFreeze();
    if (!response.error) {
      dispatch(setRdaJobRunning(response.data.rdaJobRunning || false));
      dispatch(setRunningCostJobRunning(response.data.runningCostJobRunning || false));
    } else {
      showError(get(response, "error.messageCode", "ER000"));
    }
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          className="mr-10"
          isLoading={state.downloadTimesheetReportLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          content={t("projectsManagement.planning.workCostsTab.timesheetReport")}
          clickCallback={handleExportTimesheetReport}
          hidden={
            !(
              userInfo.roles.includes("SYSTEM_IT") ||
              userInfo.roles.includes("PM") ||
              userInfo.roles.includes("SUPER_PM")
            )
          }
        />
        <Button
          className="mr-10"
          currentIcon={<UploadOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, importCostsPlanModal: true }))}
        />
        <Button
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={handleExport}
        />
      </Row>
      {!isEmpty(state.dataTable) && (
        <WCTable
          dataContentWP={state.dataTable}
          allData={state.allData}
          projectId={params.id}
          projectEditable={projectManagement.enabled}
          totalTableRef={totalTableRef}
          totalTableExpandedChange={totalTableExpandedChange}
          rdaAlertProjectTaskIds={state.rdaAlertProjectTaskIds}
          setRdaAlertProjectTaskIds={setRdaAlertProjectTaskIds}
          plannedRunningErrorProjectTaskIds={state.plannedRunningErrorProjectTaskIds}
          setPlannedRunningErrorProjectTaskIds={setPlannedRunningErrorProjectTaskIds}
        />
      )}
      {!isEmpty(state.totalAmountValues) && (
        <TotalWCTable
          dataContentTotal={state.totalAmountValues}
          id={params.id}
          totalTableRef={totalTableRef}
          notifyWCTableOnExpandChange={() => setTotalTableExpandedChange((prev) => !prev)}
        />
      )}
      <Row type="flex" align="middle" justify="end" className="mb-10 mt-10">
        <Button
          isDisabled={!(userInfo.canEditPlanning && projectManagement.enabled && !state.allData.closed && !isDirtyWC)}
          currentIcon={<CheckOutlined className="icon-medium" />}
          className="ml-10"
          clickCallback={handleRelease}
        />
        <Button
          currentIcon={<SyncOutlined className="icon-medium" />}
          className="ml-10"
          clickCallback={() => {
            dispatch(setRefreshWorkCosts(true));
            dispatch(setRefreshWorkCostsTotals(true));
          }}
        />
      </Row>
      <Modal
        width={700}
        title={<b>{t("monthPlanning.importCostsPlan")}</b>}
        open={state.importCostsPlanModal}
        onCancel={() => setState((prevState) => ({ ...prevState, importCostsPlanModal: false, fileList: [] }))}
        footer={[
          <Button
            isLoading={state.loading}
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikDocument.submitForm}
            isDisabled={!(formikDocument.dirty && formikDocument.isValid)}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 48]}>
          <Col span={24}>
            <Row type="flex" justify="center" align="center" className="mb-10">
              <Col span={24}>
                <p>
                  {t("projectEstimate.modal.allowedExtensions")} {".csv"}
                </p>
              </Col>
              <Col span={24}>
                <Dragger {...uploadInvoice}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">{t("projectEstimate.detail.tabs.preventiveForm.uploadTextEdit")}</p>
                </Dragger>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default WorkCosts;
